import { type ComponentProps } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'

import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/shared/components/ui/sidebar/sidebar'
import { useSidebar } from '@/shared/hooks'
import type { RequiredKeys } from '@/shared/interfaces'

export interface NavItem {
  title: string
  url: string
  icon: ComponentProps<typeof Icon>['name']
  children?: Array<{
    title: string
    url: string
    isActive?: boolean
  }>
}

interface INavMainProps {
  items: NavItem[]
}

interface INavItemWithSubProps {
  item: RequiredKeys<NavItem, 'children'>
  onSubItemClick: () => void
}

export function NavMain({ items }: Readonly<INavMainProps>) {
  const { t } = useTranslation()
  const location = useLocation()
  const { setOpenMobile, isMobile } = useSidebar()

  const closeMobileSidebar = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{t('layouts.sidebar.title')}</SidebarGroupLabel>
      <SidebarMenu>
        {items.map((item) => (
          <Render.If
            key={item.url}
            condition={item.children}
            fallback={
              <SidebarMenuButton tooltip={item.title} asChild isActive={location.pathname.includes(item.url)}>
                <Link to={item.url} onClick={closeMobileSidebar}>
                  <Icon name={item.icon} />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            }
          >
            {(subItem) => <NavItemWithSub item={{ ...item, children: subItem }} onSubItemClick={closeMobileSidebar} />}
          </Render.If>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}

const NavItemWithSub = ({ item, onSubItemClick }: INavItemWithSubProps) => {
  const location = useLocation()
  const { open } = useSidebar()
  const Comp = open ? 'span' : Link

  return (
    <Collapsible key={item.title} asChild className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton asChild tooltip={item.title} isActive={location.pathname.includes(item.url)}>
            <Comp to={item.children.at(0)?.url ?? item.url}>
              <Icon name={item.icon} />
              <span>{item.title}</span>
              <Icon
                name="chevron-right"
                className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
              />
            </Comp>
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children?.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton asChild isActive={location.pathname.includes(subItem.url)}>
                  <Link to={subItem.url} onClick={onSubItemClick}>
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
